import { dist2, idiv, imax, imin, isqrt, withinRange } from '../math/fixed.ts';
import type { AutumnRules, BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import { castSkill } from '../battle/skills.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { autumnHidden, autumnOrder } from '../battle/druid-autumn.ts';
import { useSkills } from './ghost.ts';

/**
 * The druid's chapter 3 played by the lab's bot (docs/SENDAS.md, «Calibración»; ai/senda-bot.ts sends it here):
 *
 * - under the leaves (`leaves`): who knows keeps by Ceda, stands still across a furrow coming at her (its Raíz pulls
 *   the stalker up) and strikes what holds her first (a cocoon, a weaver spinning); who doesn't fights the nearest it
 *   can reach and walks after Ceda.
 * - the memory (`blight`): who knows cuts each head's root ahead of it, as far up as it gets there in time (above a
 *   fork, both branches), and with no head coming, the top of the root the next one comes down; between, what is at
 *   Paz. Who doesn't fights what comes (the rot isn't a thing it can strike).
 * - what lies below (`under`, the test): who knows cuts each rotten root where it runs alone, those under the ground
 *   first (where they run alone is by the knot, and the rot passes there soon), off the broodmother's leap, the webs on
 *   the maple struck down. Who doesn't cuts the black roots it sees, one each, and then fights.
 */

type Pt = { x: number; y: number };

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

const guard = (s: BattleState, h: Hero, x: number, y: number, r: number): boolean => {
  const t = nearestTo(
    enemiesNear(s, x, y, r, (e) => targetable(s, e)),
    x,
    y,
  );
  if (!t) return false;
  strike(s, h, t);
  return true;
};

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** Away from a point, to `d` from it: of six ways round the hero, the floor farthest from it. */
const away = (s: BattleState, h: Hero, x: number, y: number, d: number): void => {
  const DX = [1000, 500, -500, -1000, -500, 500];
  const DY = [0, -866, -866, 0, 866, 866];
  let bx = h.x;
  let by = h.y;
  let bd = -1;
  for (let k = 0; k < 6; k++) {
    for (const m of [d, idiv(d * 2, 3)]) {
      const tx = h.x + idiv((DX[k] as number) * m, 1000);
      const ty = h.y + idiv((DY[k] as number) * m, 1000);
      if (!heroWalkable(s, cellAtWorld(s, tx, ty))) continue;
      const dd = dist2(tx, ty, x, y);
      if (dd > bd) {
        bd = dd;
        bx = tx;
        by = ty;
      }
      break;
    }
  }
  h.target = 0;
  go(s, h, bx, by, 150);
};

/** Ticks for the hero to walk from where it is to a point. */
const walkTicks = (h: Hero, p: Pt): number => idiv(isqrt(dist2(h.x, h.y, p.x, p.y)) * 12, imax(1, h.stats.speed));

/* ---------- under the leaves ---------- */

const leavesBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.autumn?.leaves;
  const w = s.senda?.autumn;
  const ceda = npcOf(s, 'ceda');
  if (!r || !w || !ceda) return;
  const ahead = r.way[w.cedaAt] ?? ceda;
  if (!knows) {
    if (guard(s, h, h.x, h.y, 7000)) return;
    h.target = 0;
    return go(s, h, ceda.x + 900, ceda.y + 700);
  }
  // A cocoon on her first.
  const coc = w.cocoon ? findEnemy(s, w.cocoon) : null;
  if (coc && coc.hp > 0) return strike(s, h, coc);
  // A weaver spinning at her: strike it (struck enough, it lets go).
  for (const id of Object.keys(w.wv)) {
    const m = w.wv[Number(id)] as number[];
    const e = findEnemy(s, Number(id));
    if (e && e.hp > 0 && (m[1] as number) >= 0) return strike(s, h, e);
  }
  // What is up at her (a stalker come up or held, anything else at her).
  const at = nearestTo(
    enemiesNear(s, ceda.x, ceda.y, 2600, (e) => targetable(s, e)),
    ceda.x,
    ceda.y,
  );
  if (at) {
    strike(s, h, at);
    // (Stand where it can still catch a furrow coming: by her, not after the stalker.)
    if (!withinRange(h.x, h.y, ceda.x, ceda.y, 2200)) go(s, h, ceda.x + 600, ceda.y + 500);
    return;
  }
  // A furrow coming at her: stand across its way, still (the Raíz pulls it up).
  let comer: Enemy | null = null;
  let cd = 0x7fffffff;
  for (const id of Object.keys(w.sk)) {
    const e = findEnemy(s, Number(id));
    if (!e || e.hp <= 0 || !autumnHidden(s, e)) continue;
    const d = dist2(e.x, e.y, ceda.x, ceda.y);
    if (d < cd) {
      cd = d;
      comer = e;
    }
  }
  if (comer && cd < 6500 * 6500) {
    const dx = comer.x - ceda.x;
    const dy = comer.y - ceda.y;
    const n = imax(1, isqrt(dx * dx + dy * dy));
    const k = imax(600, imin(1500, n - 600));
    const tx = ceda.x + idiv(dx * k, n);
    const ty = ceda.y + idiv(dy * k, n);
    if (!withinRange(h.x, h.y, tx, ty, 700)) {
      h.target = 0;
      return go(s, h, tx, ty, 400);
    }
    // There: still, and strike from where it stands.
    if (h.moving) orderMove(s, h, h.x, h.y);
    guard(s, h, h.x, h.y, 4200);
    return;
  }
  if (guard(s, h, ceda.x, ceda.y, 4200)) return;
  h.target = 0;
  // A step ahead of her, on her way.
  const dx = ahead.x - ceda.x;
  const dy = ahead.y - ceda.y;
  const n = imax(1, isqrt(dx * dx + dy * dy));
  go(s, h, ceda.x + idiv(dx * 900, n), ceda.y + idiv(dy * 900, n), 500);
};

/* ---------- the memory ---------- */

type Blight = NonNullable<AutumnRules['blight']>;

/** Steps from a head's cell down to each cell below it (the subtree), with the most steps to an end. */
const below = (r: Blight, i: number, out: { i: number; d: number }[], d = 0): void => {
  for (const c of (r.nodes[i] as Blight['nodes'][number]).down) {
    out.push({ i: c, d: d + 1 });
    below(r, c, out, d + 1);
  }
};

const blightBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.autumn?.blight;
  const w = s.senda?.autumn;
  const paz = npcOf(s, 'paz');
  if (!r || !w || !w.rot.length) return;
  if (!knows) {
    if (guard(s, h, h.x, h.y, 7000)) return;
    h.target = 0;
    if (paz) go(s, h, paz.x + 800, paz.y - 600);
    return;
  }
  // Mid-cut: let it be (unless something stands at Paz and the cut has just begun).
  if (w.job >= 0) {
    const p = r.nodes[w.job] as Pt;
    if (withinRange(h.x, h.y, p.x, p.y, r.reach)) {
      if (h.moving) orderMove(s, h, h.x, h.y);
      guard(s, h, h.x, h.y, 4000);
      return;
    }
    return;
  }
  // Each head: the first cell below it it gets to in time (above a fork, both are stopped); the most urgent first.
  let best = -1;
  let bestUrg = 0x7fffffff;
  for (const hd of w.heads) {
    const sub: { i: number; d: number }[] = [];
    below(r, hd.i, sub);
    // (Already stopped further down on every branch: nothing to do for it.)
    const open = sub.filter((x) => w.rot[x.i] !== 2);
    if (!sub.some((x) => r.ends.includes(x.i) && !blocked(r, w.rot, hd.i, x.i))) continue;
    sub.sort((a, b) => a.d - b.d);
    let urg = 0x7fffffff;
    for (const x of sub) if (r.ends.includes(x.i)) urg = imin(urg, x.d);
    for (const x of open) {
      const arrive = hd.at - s.tick + (x.d - 1) * r.per;
      if (walkTicks(h, r.nodes[x.i] as Pt) + r.cut + 20 <= arrive && x.d >= 2) {
        if (urg < bestUrg) {
          bestUrg = urg;
          best = x.i;
        }
        break;
      }
    }
  }
  if (best >= 0) return autumnOrder(s, h, best);
  // No head to cut: the top of the root the next wave comes down, cut before it comes.
  const nx = r.waves[w.wave];
  if (nx) {
    const top = r.srcs[nx.src] as number;
    if (w.rot[top] !== 2 && nx.t - s.waveT <= walkTicks(h, r.nodes[top] as Pt) + r.cut + 72) return autumnOrder(s, h, top);
  }
  if (paz && paz.hp > 0 && guard(s, h, paz.x, paz.y, 2600)) return;
  if (guard(s, h, h.x, h.y, 3500)) return;
  h.target = 0;
  if (nx) {
    const p = r.nodes[r.srcs[nx.src] as number] as Pt;
    go(s, h, p.x, p.y + 1200, 600);
  }
};

/** Whether a cut lies between a head's cell and a cell below it. */
const blocked = (r: Blight, rot: number[], from: number, to: number): boolean => {
  let at = to;
  while (at >= 0 && at !== from) {
    if (rot[at] === 2) return true;
    at = (r.nodes[at] as Blight['nodes'][number]).up;
  }
  return false;
};

/* ---------- what lies below ---------- */

const underBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.autumn?.under;
  const w = s.senda?.autumn;
  if (!r || !w || !w.cut.length || w.done >= 0) return;
  // Off the broodmother's leap (unless the cut is all but done).
  if (w.bt >= 0 && withinRange(h.x, h.y, w.bx, w.by, r.boss.r + 400) && !(w.job >= 0 && w.held >= r.cut - 6)) {
    w.job = -1;
    return away(s, h, w.bx, w.by, r.boss.r + 1100);
  }
  // When the rot comes to the k-th cell of a strand (ticks from now; huge while it sleeps).
  const when = (k: number, j: number): number => {
    const f = w.front[k] as number;
    if (j <= f) return -1;
    if (w.woke < 0) return 100000 + (j - f) * r.step;
    return w.next - s.tick + (j - f - 1) * r.step;
  };
  if (w.job >= 0) {
    const p = r.cells[w.job] as Pt;
    // (The rot past it on the way there: that cut is in vain now.)
    for (const k of r.on[w.job] as number[]) {
      const st = r.strands[k] as { at: number[]; rot: boolean };
      if (knows && st.rot && when(k, st.at.indexOf(w.job)) < 0) w.job = -1;
    }
    if (w.job >= 0) {
      if (withinRange(h.x, h.y, p.x, p.y, r.reach) && h.moving) orderMove(s, h, h.x, h.y);
      if (withinRange(h.x, h.y, p.x, p.y, r.reach)) guard(s, h, h.x, h.y, 3000);
      return;
    }
  }
  // A web on the maple about to take a branch.
  for (const id of Object.keys(w.webs)) {
    const c = findEnemy(s, Number(id));
    const until = w.webs[Number(id)] as number;
    if (c && c.hp > 0 && until - s.tick < 84) return strike(s, h, c);
  }
  const todo: number[] = [];
  if (knows) {
    // Each rotten root still open: the cells where it runs alone that it gets to before the rot; the root whose last
    // such cell comes soonest first, and on it the nearest.
    const plan: { i: number; due: number }[] = [];
    for (let k = 0; k < r.strands.length; k++) {
      const st = r.strands[k] as { at: number[]; rot: boolean };
      if (!st.rot || (w.block[k] as number) >= 0) continue;
      let pick = -1;
      let pd = 0x7fffffff;
      let due = -1;
      for (let j = 1; j < st.at.length; j++) {
        const i = st.at[j] as number;
        if (i < 0 || (r.on[i] as number[]).length !== 1 || w.cut[i]) continue;
        const t = when(k, j);
        if (t < 0) continue;
        if (walkTicks(h, r.cells[i] as Pt) + r.cut + 10 > t) continue;
        due = imax(due, t);
        const d = dist2(h.x, h.y, (r.cells[i] as Pt).x, (r.cells[i] as Pt).y);
        if (d < pd) {
          pd = d;
          pick = i;
        }
      }
      if (pick >= 0) plan.push({ i: pick, due });
    }
    plan.sort((a, b) => a.due - b.due);
    for (const p of plan) todo.push(p.i);
  } else {
    // The black roots in sight, the first of each after the knot.
    for (let k = 0; k < r.strands.length; k++) {
      const st = r.strands[k] as { at: number[]; rot: boolean };
      if (!st.rot || (w.block[k] as number) >= 0) continue;
      const vis = visibleOf(s, k);
      if (vis >= 0 && !w.cut[vis]) todo.push(vis);
    }
  }
  if (todo.length) {
    // Close by things first if they are at the hero.
    const on = enemiesNear(s, h.x, h.y, 1300, (e) => targetable(s, e));
    if (on.length >= 3) return strike(s, h, nearestTo(on, h.x, h.y) as Enemy);
    return autumnOrder(s, h, todo[0] as number);
  }
  if (guard(s, h, h.x, h.y, 3000)) return;
  const boss = w.boss ? findEnemy(s, w.boss) : null;
  if (boss && boss.hp > 0 && targetable(s, boss)) return strike(s, h, boss);
  if (guard(s, h, h.x, h.y, 8000)) return;
};

/** The first cell in sight of a rotten root after the knot (−1: it runs under all the way). */
const visibleOf = (s: BattleState, k: number): number => {
  const v = s.rules?.senda?.autumn?.under?.vis;
  const st = s.rules?.senda?.autumn?.under?.strands[k];
  if (!v || !st) return -1;
  for (const i of st.at) if (i >= 0 && v[i] === 1) return i;
  return -1;
};

export const autumnBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.autumn;
  if (!r) return;
  if (r.leaves) return leavesBot(s, h, knows);
  if (r.blight) return blightBot(s, h, knows);
  if (r.under) return underBot(s, h, knows);
};
